// Maintenance.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import "./Maintenance.css";

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const STATUS_TABS = ["All", "Complete", "Scheduled", "Unscheduled"];

const emptyData = {
  kpis: { scheduledMaintenance: 0, upcomingMaintenance: 0 },
  rows: [],
};

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

const getStatusTone = (status) => {
  const s = String(status || "").toUpperCase();
  if (s === "COMPLETE") return "complete";
  if (s === "SCHEDULED") return "scheduled";
  if (s === "UNSCHEDULED") return "upcoming";
  return "neutral";
};

const parseMDY = (str) => {
  if (!str) return null;
  const parts = str.split("/");
  if (parts.length !== 3) return null;
  const d = new Date(Number(parts[2]), Number(parts[0]) - 1, Number(parts[1]));
  return Number.isNaN(d.getTime()) ? null : d;
};

const parseYMD = (str) => {
  if (!str) return null;
  const d = new Date(str + "T00:00:00");
  return Number.isNaN(d.getTime()) ? null : d;
};

const matchesSearch = (row, term) => {
  if (!term) return true;
  const lower = term.toLowerCase();
  return [
    row.workOrder,
    row.description,
    row.type,
    row.status,
    row.serviceAgreement,
    row.property,
    row.maintenanceDueDate,
    row.completedDate,
  ]
    .filter(Boolean)
    .some((v) => String(v).toLowerCase().includes(lower));
};

const matchesDateRange = (dateStr, fromDate, toDate) => {
  if (!fromDate && !toDate) return true;
  const date = parseMDY(dateStr);
  if (!date) return !fromDate && !toDate;
  if (fromDate && date < fromDate) return false;
  if (toDate && date > toDate) return false;
  return true;
};

/* -------------------------------------------------------------------------- */
/*                              Reusable UI pieces                            */
/* -------------------------------------------------------------------------- */

const KpiCard = ({ label, value, accent, isLoading }) => (
  <div className={`maint-kpi-card ${accent ? `maint-kpi-card--${accent}` : ""}`}>
    <div className="maint-kpi-card__label">{label}</div>
    <div className="maint-kpi-card__value">
      {isLoading ? <span className="card-spinner" /> : value}
    </div>
  </div>
);

const SkeletonRows = ({ cols, count = 6 }) =>
  Array.from({ length: count }).map((_, i) => (
    <tr key={i}>
      {Array.from({ length: cols }).map((_, j) => (
        <td key={j}><span className="skeleton-line" /></td>
      ))}
    </tr>
  ));

const CompanySelect = ({ customers, selectedCustomerId, setSelectedCustomerId, disabled }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  useEffect(() => {
    const onClickOutside = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);
  const selected = customers.find((c) => String(c.id) === String(selectedCustomerId));
  const buttonLabel = selected ? selected.name : "All Companies";
  return (
    <div ref={wrapperRef} className="maint-filter-field">
      <label className="maint-filter-field__label">Company Selection</label>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((p) => !p)}
        className="maint-select-btn"
      >
        <span className="maint-select-btn__text">{buttonLabel}</span>
        <span className={`maint-select-btn__chevron ${open ? "is-open" : ""}`}>▾</span>
      </button>
      {open && !disabled ? (
        <div className="maint-multiselect-menu">
          <div className="maint-multiselect-list">
            <div
              className={`maint-checkbox-row${!selectedCustomerId ? " is-selected" : ""}`}
              onClick={() => { setSelectedCustomerId(""); setOpen(false); }}
            >
              <span>All Companies</span>
            </div>
            {customers.map((c) => (
              <div
                key={c.id}
                className={`maint-checkbox-row${String(selectedCustomerId) === String(c.id) ? " is-selected" : ""}`}
                onClick={() => { setSelectedCustomerId(String(c.id)); setOpen(false); }}
              >
                <span>{c.name}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const PropertyMultiSelect = ({ properties, selectedPropertyIds, setSelectedPropertyIds, disabled }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onOutside = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  const selectedNames = properties
    .filter((p) => selectedPropertyIds.includes(String(p.id)))
    .map((p) => p.name);

  const buttonLabel = (() => {
    if (!selectedPropertyIds.length) return "Select values";
    if (selectedPropertyIds.length === 1) return selectedNames[0] || "1 Property";
    return `${selectedPropertyIds.length} selected`;
  })();

  const toggle = (id) => {
    setSelectedPropertyIds((curr) => {
      const s = String(id);
      return curr.includes(s) ? curr.filter((x) => x !== s) : [...curr, s];
    });
  };

  return (
    <div ref={wrapperRef} className="maint-filter-field">
      <label className="maint-filter-field__label">Property Selection</label>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((p) => !p)}
        className="maint-select-btn"
      >
        <span className="maint-select-btn__text">{buttonLabel}</span>
        <span className={`maint-select-btn__chevron ${open ? "is-open" : ""}`}>▾</span>
      </button>

      {open && !disabled ? (
        <div className="maint-multiselect-menu">
          <div className="maint-multiselect-actions">
            <button type="button" className="maint-pill-btn" onClick={() => setSelectedPropertyIds([])}>All</button>
            <button type="button" className="maint-pill-btn" onClick={() => setSelectedPropertyIds(properties.map((p) => String(p.id)))}>Select All</button>
          </div>
          <div className="maint-multiselect-list">
            {properties.length ? (
              properties.map((p) => (
                <label key={p.id} className="maint-checkbox-row">
                  <input type="checkbox" checked={selectedPropertyIds.includes(String(p.id))} onChange={() => toggle(p.id)} />
                  <span>{p.name}</span>
                </label>
              ))
            ) : (
              <div className="maint-multiselect-empty">No properties available.</div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const DateRangeFilter = ({ label, fromDate, toDate, setFromDate, setToDate }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onOutside = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  const hasValue = fromDate || toDate;
  const displayLabel = (() => {
    if (fromDate && toDate) return `${fromDate} – ${toDate}`;
    if (fromDate) return `From ${fromDate}`;
    if (toDate) return `To ${toDate}`;
    return "Select date range";
  })();

  return (
    <div ref={wrapperRef} className="maint-filter-field">
      <label className="maint-filter-field__label">{label}</label>
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className={`maint-select-btn ${hasValue ? "has-value" : ""}`}
      >
        <span className="maint-date-icon">&#128197;</span>
        <span className="maint-select-btn__text">{displayLabel}</span>
        <span className={`maint-select-btn__chevron ${open ? "is-open" : ""}`}>▾</span>
      </button>

      {open ? (
        <div className="maint-date-menu">
          <div className="maint-date-row">
            <label className="maint-date-label">From</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="maint-date-input" />
          </div>
          <div className="maint-date-row">
            <label className="maint-date-label">To</label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="maint-date-input" />
          </div>
          {hasValue ? (
            <button type="button" className="maint-pill-btn" onClick={() => { setFromDate(""); setToDate(""); }}>Clear</button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*                                 Maintenance                                */
/* -------------------------------------------------------------------------- */

const Maintenance = ({ viewerEmail }) => {
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [properties, setProperties] = useState([]);

  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [selectedPropertyIds, setSelectedPropertyIds] = useState([]);

  const [filterError, setFilterError] = useState("");
  const [dataError, setDataError] = useState("");

  const [data, setData] = useState(emptyData);

  const [activeStatusTab, setActiveStatusTab] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [completedDateFrom, setCompletedDateFrom] = useState("");
  const [completedDateTo, setCompletedDateTo] = useState("");
  const [selectedSA, setSelectedSA] = useState(null);

  // Load filters
  useEffect(() => {
    const load = async () => {
      try {
        setFiltersLoading(true);
        setFilterError("");
        const token = localStorage.getItem("token");
        if (!token) { setFilterError("No auth token found."); return; }

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/maintenance/maintenance-filters`,
          { headers: { Authorization: `Bearer ${token}` }, params: viewerEmail ? { email: viewerEmail } : undefined }
        );
        const d = res?.data || {};
        setCustomers(Array.isArray(d.customers) ? d.customers : []);
        setProperties(Array.isArray(d.properties) ? d.properties : []);
      } catch (err) {
        console.error("Failed to load maintenance filters:", err);
        setFilterError("Failed to load filters.");
      } finally {
        setFiltersLoading(false);
      }
    };
    load();
  }, [viewerEmail]);

  const filteredProperties = useMemo(() => {
    if (!selectedCustomerId) return properties;
    return properties.filter((p) => String(p.customerId ?? "") === String(selectedCustomerId));
  }, [properties, selectedCustomerId]);

  useEffect(() => {
    const allowed = new Set(filteredProperties.map((p) => String(p.id)));
    setSelectedPropertyIds((curr) => curr.filter((id) => allowed.has(id)));
  }, [filteredProperties]);

  // Load data
  useEffect(() => {
    const load = async () => {
      if (filtersLoading || filterError) return;
      try {
        setDataLoading(true);
        setDataError("");
        const token = localStorage.getItem("token");
        if (!token) { setDataError("No auth token found."); return; }

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/maintenance/maintenance-data`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              ...(viewerEmail ? { email: viewerEmail } : {}),
              ...(selectedCustomerId ? { customerId: selectedCustomerId } : {}),
              ...(selectedPropertyIds.length ? { propertyIds: selectedPropertyIds.join(",") } : {}),
            },
          }
        );
        setData(res?.data || emptyData);
        setSelectedSA(null);
      } catch (err) {
        console.error("Failed to load maintenance data:", err);
        setDataError("Failed to load maintenance data.");
        setData(emptyData);
      } finally {
        setDataLoading(false);
      }
    };
    load();
  }, [filtersLoading, filterError, viewerEmail, selectedCustomerId, selectedPropertyIds]);

  // Unique (SA, property) pairs — only active service agreements
  const saRows = useMemo(() => {
    const seen = new Set();
    const result = [];
    for (const row of data.rows || []) {
      if (!row.serviceAgreement) continue;
      const status = String(row.saStatus || "").toUpperCase();
      if (status && status !== "ACTIVE") continue;
      const key = `${row.serviceAgreement}||${row.property}`;
      if (!seen.has(key)) {
        seen.add(key);
        result.push({ saName: row.serviceAgreement, property: row.property });
      }
    }
    return result.sort((a, b) => a.saName.localeCompare(b.saName));
  }, [data.rows]);

  const completedDateFromObj = useMemo(() => parseYMD(completedDateFrom), [completedDateFrom]);
  const completedDateToObj = useMemo(() => parseYMD(completedDateTo), [completedDateTo]);

  const visibleRows = useMemo(() => {
    let rows = data.rows || [];

    if (selectedSA) {
      rows = rows.filter((r) => r.serviceAgreement === selectedSA);
    }
    if (activeStatusTab !== "All") {
      rows = rows.filter(
        (r) => String(r.status || "").toUpperCase() === activeStatusTab.toUpperCase()
      );
    }
    rows = rows.filter((r) => matchesDateRange(r.completedDate, completedDateFromObj, completedDateToObj));
    rows = rows.filter((r) => matchesSearch(r, searchTerm));
    return rows;
  }, [data.rows, selectedSA, activeStatusTab, completedDateFromObj, completedDateToObj, searchTerm]);

  const handleClearSearch = () => {
    setSelectedCustomerId("");
    setSelectedPropertyIds([]);
    setActiveStatusTab("All");
    setSearchTerm("");
    setCompletedDateFrom("");
    setCompletedDateTo("");
    setSelectedSA(null);
  };

  const isLoading = filtersLoading || dataLoading;

  return (
    <div className="maint-page">
      <div className="maint-shell">

        {/* ---- Filter toolbar ---- */}
        <section className="maint-toolbar">
          <div className="maint-toolbar__grid">
            <CompanySelect
              customers={customers}
              selectedCustomerId={selectedCustomerId}
              setSelectedCustomerId={setSelectedCustomerId}
              disabled={filtersLoading || !!filterError}
            />

            <PropertyMultiSelect
              properties={filteredProperties}
              selectedPropertyIds={selectedPropertyIds}
              setSelectedPropertyIds={setSelectedPropertyIds}
              disabled={filtersLoading || !!filterError}
            />

            <DateRangeFilter
              label="Date Completed Filter"
              fromDate={completedDateFrom}
              toDate={completedDateTo}
              setFromDate={setCompletedDateFrom}
              setToDate={setCompletedDateTo}
            />

            <div className="maint-filter-field">
              <label className="maint-filter-field__label">Search</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="maint-search-input"
                placeholder=""
              />
            </div>

            <div className="maint-toolbar__clear">
              <button type="button" onClick={handleClearSearch} className="maint-clear-btn">
                Clear Search
              </button>
            </div>
          </div>
        </section>

        {/* ---- Feedback ---- */}
        {filterError ? <div className="maint-feedback maint-feedback--error">{filterError}</div> : null}
        {dataError ? <div className="maint-feedback maint-feedback--error">{dataError}</div> : null}

        {!filterError ? (
          <>
            {/* ---- Hint row ---- */}
            <div className="maint-hint-row">
              <p className="maint-hint-text">
                Click on a service agreement to filter the maintenance related to that agreement.
              </p>
              <button
                type="button"
                className="maint-sa-clear-btn"
                onClick={() => setSelectedSA(null)}
                disabled={!selectedSA}
              >
                Clear Maintenance Filter
              </button>
            </div>

            {/* ---- Summary: SA table + KPI cards ---- */}
            <section className="maint-summary-grid">

              {/* Active Service Agreements */}
              <div className="maint-sa-card">
                <div className="maint-sa-title">Active Service Agreements</div>
                <div className="maint-sa-table-wrap">
                  {isLoading || saRows.length > 0 ? (
                    <table className="maint-sa-table">
                      <tbody>
                        {isLoading ? <SkeletonRows cols={2} count={4} /> : saRows.map(({ saName, property }, idx) => {
                          const isSelected = selectedSA === saName;
                          return (
                            <tr
                              key={`${saName}||${property}||${idx}`}
                              className={`maint-sa-row ${isSelected ? "is-selected" : ""}`}
                              onClick={() => setSelectedSA(isSelected ? null : saName)}
                            >
                              <td className="maint-sa-name">{saName}</td>
                              <td className="maint-sa-property">{property}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <div className="maint-sa-empty">No active service agreements</div>
                  )}
                </div>
              </div>

              {/* KPI cards — side by side */}
              <KpiCard label="Scheduled Maintenance" value={data.kpis?.scheduledMaintenance ?? 0} accent="scheduled" isLoading={isLoading} />
              <KpiCard label="Upcoming Maintenance" value={data.kpis?.upcomingMaintenance ?? 0} accent="upcoming" isLoading={isLoading} />
            </section>

            {/* ---- Table card ---- */}
            <section className="maint-table-card">
              <div className="maint-tab-group">
                <span className="maint-tab-group__label">Maintenance Status</span>
                <div className="maint-tabs">
                  {STATUS_TABS.map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setActiveStatusTab(tab)}
                      className={`maint-tab ${activeStatusTab === tab ? "is-active" : ""}`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
              </div>

              <div className="maint-table-card__sub-header">
                <span className="maint-table-card__title">All Maintenance</span>
              </div>

              <div className="maint-table-wrap">
                {isLoading || visibleRows.length > 0 ? (
                  <table className="maint-table">
                    <thead>
                      <tr>
                        <th>Maintenance</th>
                        <th>Description</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>PM Date</th>
                        <th>Service Agreement</th>
                        <th>Property</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? <SkeletonRows cols={7} /> : visibleRows.map((row, idx) => (
                        <tr key={row.jobId || `row-${idx}`} className={idx % 2 === 0 ? "" : "is-alt"}>
                          <td><div className="maint-primary-cell">{!row.workOrder ? "—" : row.reportUrl ? (<a href={row.reportUrl} target="_blank" rel="noopener noreferrer" className="maint-primary-link">{row.workOrder}</a>) : row.workOrder}</div></td>
                          <td className="maint-description-cell">{row.description || "—"}</td>
                          <td>{row.type || "—"}</td>
                          <td>
                            <span className={`maint-status-pill maint-status-pill--${getStatusTone(row.status)}`}>
                              {row.status || "—"}
                            </span>
                          </td>
                          <td>{row.maintenanceDueDate || "—"}</td>
                          <td>{row.serviceAgreement || "—"}</td>
                          <td>{row.property || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="maint-empty-state">No maintenance records found</div>
                )}
              </div>
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default Maintenance;
